import { Link } from "react-router-dom";

// Rendered verbatim on a public page and quoted in the Shopify app listing.
// Operator, address and email are the live Stripe account's legal business details.
const OPERATOR = "HobbyScan LLC";
const OPERATOR_ADDRESS = "30 North Gould Street, Sheridan, WY 82801, United States";
const CONTACT_EMAIL = "hobby@doejo.com";
// Governs the "Your rights" section — the wording assumes UK/EU GDPR + CCPA cover
// the merchant base. Narrow or widen it once the operating jurisdiction is settled.
const EFFECTIVE_DATE = "October 23, 2026";
// These mirror the RETENTION_* defaults in backend/src/config/env.ts, which is what
// the scheduled purge actually enforces. Change one and you must change the other —
// a period stated here that the sweep does not apply makes this policy false.
const PAID_CUSTOMER_CART_RETENTION = "12 months";
const OPEN_CUSTOMER_CART_RETENTION = "90 days";
const CART_RETENTION = "30 days";

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="space-y-3">
      <h2 className="text-lg font-semibold text-white">{title}</h2>
      <div className="space-y-3 text-sm leading-relaxed text-muted">{children}</div>
    </section>
  );
}

export function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-app px-4 py-12">
      <div className="mx-auto max-w-3xl space-y-8">
        <header className="space-y-2">
          <h1 className="text-2xl font-bold text-white">HobbyScan Privacy Policy</h1>
          <p className="text-sm text-muted">
            Effective {EFFECTIVE_DATE}. Operated by {OPERATOR}.
          </p>
        </header>

        <div className="panel space-y-8 p-8">
          <Section id="scope" title="1. Who this policy is for">
            <p>
              HobbyScan is a trading-card scanning, pricing and inventory app for Shopify merchants. It scans card
              images, estimates their value, and turns approved cards into Shopify products and draft orders.
            </p>
            <p>This policy covers two groups:</p>
            <ul className="list-disc space-y-1 pl-5">
              <li>
                <strong>Merchants and their staff</strong> — the people who install HobbyScan, sign in to it, and use it
                in the Shopify admin or in Shopify POS.
              </li>
              <li>
                <strong>A merchant's customers</strong> — shoppers whose orders pass through the app. As explained in
                section 3, HobbyScan does not store personal data about these shoppers.
              </li>
              <li>
                <strong>Buyers in the HobbyScan app</strong> — people with a HobbyScan account who buy from shops in the
                app, for example a spot in a live break. For their account and saved payment methods, {OPERATOR} is the
                controller.
              </li>
            </ul>
            <p>
              Where HobbyScan processes personal data on a merchant's behalf, the merchant is the data controller and{" "}
              {OPERATOR} is the processor. For merchant and staff account data, {OPERATOR} is the controller.
            </p>
          </Section>

          <Section id="data" title="2. What we process">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[34rem] border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b border-line text-white">
                    <th className="py-2 pr-4 font-semibold">Category</th>
                    <th className="py-2 pr-4 font-semibold">What it includes</th>
                    <th className="py-2 font-semibold">Why</th>
                  </tr>
                </thead>
                <tbody className="align-top">
                  <tr className="border-b border-line">
                    <td className="py-3 pr-4 font-medium text-white">Account</td>
                    <td className="py-3 pr-4">
                      Email address, display name, whether the email is verified, and an authentication identifier.
                    </td>
                    <td className="py-3">To sign you in and link you to your shop.</td>
                  </tr>
                  <tr className="border-b border-line">
                    <td className="py-3 pr-4 font-medium text-white">Shop</td>
                    <td className="py-3 pr-4">
                      Your myshopify.com domain, the access token issued at install, granted permission scopes, and your
                      shop's location addresses and coordinates.
                    </td>
                    <td className="py-3">To call the Shopify Admin API on your behalf and to show nearby shops.</td>
                  </tr>
                  <tr className="border-b border-line">
                    <td className="py-3 pr-4 font-medium text-white">Card scans</td>
                    <td className="py-3 pr-4">
                      Photographs of trading cards you or your customers submit, the card details extracted from them,
                      and estimated prices.
                    </td>
                    <td className="py-3">The core function of the app: identifying and pricing cards.</td>
                  </tr>
                  <tr className="border-b border-line">
                    <td className="py-3 pr-4 font-medium text-white">Catalogue and orders</td>
                    <td className="py-3 pr-4">
                      Products, variants, inventory levels and draft orders in your shop, plus customer carts created in
                      the app.
                    </td>
                    <td className="py-3">To create listings, adjust stock and build draft orders.</td>
                  </tr>
                  <tr className="border-b border-line">
                    <td className="py-3 pr-4 font-medium text-white">Saved payment methods</td>
                    <td className="py-3 pr-4">
                      Only when a buyer chooses to save a card: a Stripe customer reference linked to their HobbyScan
                      account, and the card's brand, last four digits and expiry date. Full card numbers are held by
                      Stripe and never reach our servers.
                    </td>
                    <td className="py-3">
                      To let a buyer pay again in one tap at any shop in the app, without re-entering the card.
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3 pr-4 font-medium text-white">Diagnostics</td>
                    <td className="py-3 pr-4">
                      Error reports and technical logs, including IP address and device or browser details.
                    </td>
                    <td className="py-3">To keep the app working and investigate faults.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Section>

          <Section id="customers" title="3. Your customers' personal data">
            <p>
              HobbyScan does not store personal data about your customers. There is no shopper name, email address,
              phone number or shipping address anywhere in our database.
            </p>
            <p>
              Order notifications sent to us by Shopify do contain customer details, because Shopify includes them in
              the notification. When one arrives, we read only the reference codes we attached to the order ourselves,
              so that the right customer cart can be marked as paid. Everything else in the notification, including all
              customer details, is discarded and never written to our database or our logs.
            </p>
            <p>
              Where a card scan is associated with a walk-in customer, it is linked to a HobbyScan account, not to a
              Shopify customer record.
            </p>
          </Section>

          <Section id="not" title="4. What we do not do">
            <ul className="list-disc space-y-1 pl-5">
              <li>We do not sell or share personal data for money or advertising.</li>
              <li>We do not use personal data for advertising or marketing profiles.</li>
              <li>We do not use personal data beyond the purposes described in section 2.</li>
              <li>
                We use automated image recognition to identify cards and suggest prices. It analyses photographs of
                cards, not people, and produces no decision with a legal or similarly significant effect on any
                individual. A merchant reviews and can edit every result before it is used.
              </li>
            </ul>
          </Section>

          <Section id="processors" title="5. Service providers">
            <p>We use the following providers to run HobbyScan. Each receives only what it needs for its function.</p>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[30rem] border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b border-line text-white">
                    <th className="py-2 pr-4 font-semibold">Provider</th>
                    <th className="py-2 pr-4 font-semibold">Purpose</th>
                    <th className="py-2 font-semibold">What it receives</th>
                  </tr>
                </thead>
                <tbody className="align-top">
                  <tr className="border-b border-line">
                    <td className="py-3 pr-4 font-medium text-white">Google Firebase</td>
                    <td className="py-3 pr-4">Authentication</td>
                    <td className="py-3">Email address, display name</td>
                  </tr>
                  <tr className="border-b border-line">
                    <td className="py-3 pr-4 font-medium text-white">Stripe</td>
                    <td className="py-3 pr-4">Payments, and saved payment methods a buyer chooses to keep</td>
                    <td className="py-3">Payment card details, billing details, amount and order reference</td>
                  </tr>
                  <tr className="border-b border-line">
                    <td className="py-3 pr-4 font-medium text-white">Amazon Web Services</td>
                    <td className="py-3 pr-4">Image storage</td>
                    <td className="py-3">Card photographs</td>
                  </tr>
                  <tr className="border-b border-line">
                    <td className="py-3 pr-4 font-medium text-white">OpenAI</td>
                    <td className="py-3 pr-4">Card recognition</td>
                    <td className="py-3">Card photographs</td>
                  </tr>
                  <tr className="border-b border-line">
                    <td className="py-3 pr-4 font-medium text-white">eBay, SportsCardsPro and PriceCharting</td>
                    <td className="py-3 pr-4">Price lookups</td>
                    <td className="py-3">Card names and attributes — no personal data</td>
                  </tr>
                  <tr className="border-b border-line">
                    <td className="py-3 pr-4 font-medium text-white">Google Maps (Geocoding API)</td>
                    <td className="py-3 pr-4">Geocoding shop locations</td>
                    <td className="py-3">Shop location addresses — no personal data</td>
                  </tr>
                  <tr className="border-b border-line">
                    <td className="py-3 pr-4 font-medium text-white">OpenStreetMap</td>
                    <td className="py-3 pr-4">Map tiles for the shop map</td>
                    <td className="py-3">IP address, the map area being viewed</td>
                  </tr>
                  <tr>
                    <td className="py-3 pr-4 font-medium text-white">Sentry</td>
                    <td className="py-3 pr-4">Error monitoring</td>
                    <td className="py-3">Error reports, IP address, device details</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p>
              Some of these providers operate outside your country. Where personal data is transferred internationally,
              we rely on the transfer safeguards offered by the provider, such as standard contractual clauses.
            </p>
          </Section>

          <Section id="retention" title="6. How long we keep data">
            <ul className="list-disc space-y-1 pl-5">
              <li>
                <strong>On uninstall</strong> — your shop's access token is deleted immediately and becomes unusable.
              </li>
              <li>
                <strong>48 hours after uninstall</strong> — Shopify asks us to erase your shop, and we delete the shop
                record together with every scan, product link, customer cart and stored image belonging to it.
              </li>
              <li>
                <strong>Completed customer carts</strong> — deleted {PAID_CUSTOMER_CART_RETENTION} after payment.
              </li>
              <li>
                <strong>Abandoned customer carts</strong> — deleted after {OPEN_CUSTOMER_CART_RETENTION} without
                progress.
              </li>
              <li>
                <strong>Abandoned carts</strong> — deleted after {CART_RETENTION}, along with the matching draft order
                in your Shopify admin.
              </li>
              <li>
                <strong>Card scans</strong> — kept for as long as you want them. Scans are records of trading cards
                rather than of people, and merchants rely on the history, so they are not deleted on a timer. You can
                delete any scan yourself, and all of a shop's scans are erased when the app is uninstalled.
              </li>
              <li>
                <strong>Account data</strong> — kept while your account is active, and deleted on request.
              </li>
              <li>
                <strong>Saved payment methods</strong> — kept until the buyer removes them in the app or deletes their
                account. A saved card is never charged without the buyer confirming the purchase.
              </li>
              <li>
                <strong>Diagnostics</strong> — kept for a short period for fault investigation, then discarded.
              </li>
            </ul>
          </Section>

          <Section id="security" title="7. How we protect data">
            <ul className="list-disc space-y-1 pl-5">
              <li>All traffic to and from the app is encrypted with HTTPS.</li>
              <li>
                Data is encrypted at rest. Shopify access tokens receive an additional layer of application-level
                encryption, so they are unreadable even to someone holding a copy of the database.
              </li>
              <li>Every notification we accept from Shopify is cryptographically verified before it is acted on.</li>
              <li>Access to production systems is limited to personnel who need it.</li>
            </ul>
          </Section>

          <Section id="rights" title="8. Your rights">
            <p>
              Depending on where you live, you may have the right to access, correct, delete, or receive a copy of your
              personal data, to object to or restrict how it is used, and to withdraw consent. You also have the right
              to complain to your data protection regulator.
            </p>
            <p>
              To exercise any of these rights, contact us at {CONTACT_EMAIL}. We respond within the period required by
              the applicable law.
            </p>
            <p>
              If you are a customer of a shop that uses HobbyScan, contact that shop first. They are the controller of
              their customer data, and we will assist them in responding to you. Requests routed to us through Shopify's
              privacy channels are handled automatically.
            </p>
            <p>We do not sell personal data, so there is nothing to opt out of in that respect.</p>
          </Section>

          <Section id="agreement" title="9. Agreement with merchants">
            <p>
              By installing HobbyScan you accept this policy, which together with Shopify's API Terms of Service and
              Privacy Policy governs how we handle data on your behalf. If you require a separate data processing
              agreement, contact us at {CONTACT_EMAIL}.
            </p>
          </Section>

          <Section id="children" title="10. Children">
            <p>
              HobbyScan is a tool for merchants and is not directed at children. We do not knowingly collect personal
              data from children.
            </p>
          </Section>

          <Section id="changes" title="11. Changes to this policy">
            <p>
              We update this policy when our practices change. The effective date at the top shows the current version.
              Material changes will be communicated to merchants before they take effect.
            </p>
          </Section>

          <Section id="contact" title="12. Contact us">
            <p>
              {OPERATOR}
              <br />
              {OPERATOR_ADDRESS}
              <br />
              {CONTACT_EMAIL}
            </p>
          </Section>
        </div>

        <footer className="pb-4 text-center text-sm">
          <Link to="/login" className="text-accent hover:underline">
            Back to sign in
          </Link>
        </footer>
      </div>
    </div>
  );
}
